import {
  Outlet,
  createRootRoute,
  type ErrorComponentProps,
} from "@tanstack/react-router";
// import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useConvexAuth, useQuery } from "convex/react";
import type { ConvexReactClient } from "convex/react";
import { lazy, Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import ErrorDisplay from "@/components/ui/ErrorDisplay";
import UpdateAwareErrorDisplay from "@/components/ui/UpdateAwareErrorDisplay";
import { Button } from "@/components/shadcn/button";
import { api } from "@/../convex/_generated/api";
import { identifyUser, reportError } from "@/lib/analytics";
import { applyLanguage } from "@/i18n";
import CommandCenter from "@/components/command-center/CommandCenter";
import VoiceMemoController from "@/components/voice-memo/VoiceMemoController";
import { useLoadingScreenStore } from "@/stores/loadingScreenStore";
import { TEMPLATE_SURFACES, useTemplateEditor } from "@/hooks/useTemplateEditor";

// Chargé à la demande : l'éditeur embarque BlockNote, le registre des fields et
// le drag & drop des layouts, que la plupart des sessions n'ouvrent jamais.
const TemplateEditorModal = lazy(
  () => import("@/components/settings/templates/TemplateEditorModal"),
);

export interface RouterContext {
  convex: ConvexReactClient;
}

// Param de recherche global : l'éditeur de template s'ouvre depuis le canvas
// comme depuis les settings, donc son schéma est déclaré ici plutôt que sur
// chaque route. `template` vaut un id de template, ou "new" pour une
// création ; `templateLayout`, le layout qu'il montre d'abord. Tolérés
// partout, ignorés par les routes qui n'en font rien.
const rootSearchSchema = z.object({
  template: z.string().optional(),
  // Un lien abîmé ne doit pas casser la page : la valeur est ignorée.
  templateLayout: z.enum(TEMPLATE_SURFACES).optional().catch(undefined),
});

export const Route = createRootRoute({
  component: RootComponent,
  validateSearch: rootSearchSchema,
  errorComponent: RootErrorComponent,
  notFoundComponent: RootNotFoundComponent,
});

/**
 * Sans ça, une erreur levée pendant le render d'une route (typiquement un
 * `useQuery` Convex dont la query échoue : il throw pendant le render) remonte
 * jusqu'à l'`AppErrorBoundary` et l'utilisateur perd toute l'app. Ici on
 * n'écrase que le contenu de la route.
 */
function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  useEffect(() => {
    reportError(error, { source: "router.errorComponent" });
  }, [error]);
  useReleaseBootLoader();
  const { t } = useTranslation(["shell", "common"]);

  // `autoReload` : la route entière est déjà démontée, recharger ne fait rien
  // perdre. Si un déploiement explique l'erreur, la page se met à jour seule.
  return (
    <div className="h-screen w-screen">
      <UpdateAwareErrorDisplay
        title={t("root.error.title")}
        error={error instanceof Error ? error : null}
        reloadLabel={t("common:actions.reload")}
        autoReload
        secondaryActions={
          <>
            <Button variant="outline" onClick={reset}>
              {t("common:actions.tryAgain")}
            </Button>
            {/* Navigation dure : `reset` re-rend la *même* route, donc une route
                définitivement cassée (canvas supprimé, accès révoqué) boucle sur
                l'erreur. Repartir de `/` est la seule sortie fiable. */}
            <Button
              variant="outline"
              onClick={() => window.location.assign("/")}
            >
              {t("root.backToCanvases")}
            </Button>
          </>
        }
      />
    </div>
  );
}

function RootNotFoundComponent() {
  useReleaseBootLoader();
  const { t } = useTranslation(["shell", "common"]);
  return (
    <div className="h-screen w-screen">
      <ErrorDisplay
        title={t("root.notFound.title")}
        message={t("root.notFound.message")}
        cta={
          <Button onClick={() => window.location.assign("/")}>
            {t("root.backToCanvases")}
          </Button>
        }
      />
    </div>
  );
}

/**
 * Une erreur de route remplace le canvas attendu : `BootLoader` ne doit pas
 * l'attendre plus longtemps.
 */
function useReleaseBootLoader() {
  useEffect(() => {
    useLoadingScreenStore.getState().finishBoot();
  }, []);
}

function RootComponent() {
  const { isLoading } = useConvexAuth();
  const me = useQuery(api.users.me);

  // Rattache les erreurs remontées à un utilisateur plutôt qu'à un navigateur
  // anonyme. `me` est `undefined` tant que la query charge, `null` si personne
  // n'est connecté (canvas public).
  useEffect(() => {
    if (!me) return;
    identifyUser(me._id, {
      email: me.email,
      name: me.displayName ?? undefined,
    });
  }, [me]);

  // La langue du compte l'emporte sur celle du navigateur : appliquée dès
  // qu'elle arrive, elle suit l'utilisateur d'un appareil à l'autre. Sans
  // langue enregistrée, on garde celle détectée au démarrage.
  const accountLanguage = me?.language;
  useEffect(() => {
    if (accountLanguage) applyLanguage(accountLanguage);
  }, [accountLanguage]);

  // Rien à peindre : `BootLoader` couvre l'écran tant que l'auth charge.
  if (isLoading) return null;

  return (
    // `overflow-hidden` + `overscroll-x-none` : le shell ne scrolle jamais
    // lui-même (home/settings scrollent en interne en Y) et un swipe trackpad
    // horizontal ne doit pas devenir un "back" navigateur (cf. index.css).
    // `h-dvh` (et non `h-screen` = 100vh) : sur mobile la barre d'URL fait
    // dépasser 100vh du viewport visible, le bas de la home restait coincé
    // derrière le chrome navigateur.
    <div className="h-dvh w-screen overflow-hidden overscroll-x-none">
      <Outlet />
      {/* Monté une seule fois, ici : l'éditeur de template s'ouvre depuis le
          canvas (clic droit sur un custom node) comme depuis les settings. */}
      <TemplateEditorHost />
      {/* Idem : le command center (Ctrl/Cmd + P) doit répondre depuis
          n'importe quelle route, pas seulement depuis un canvas. */}
      <CommandCenter />
      {/* Un mémo vocal arrêté en quittant son canvas s'upload et se crée
          encore une fois le canvas démonté : son contrôleur vit ici. */}
      <VoiceMemoController />
      {/* <TanStackRouterDevtools position="bottom-right" /> */}
    </div>
  );
}

/**
 * Ne télécharge l'éditeur qu'une fois `?template=` posé. La modale reste montée
 * tant que le param est là : sa garde de navigation (`useBlocker`) vit avec.
 */
function TemplateEditorHost() {
  const { editingTemplateId } = useTemplateEditor();
  if (!editingTemplateId) return null;
  return (
    <Suspense fallback={null}>
      <TemplateEditorModal />
    </Suspense>
  );
}
